import { createSelector } from '@ngrx/store';

import * as fromFeature from '../reducers';
import * as fromInputs from '../reducers/inputs.reducer';
import { environment } from 'environments/environment';
import { BRBounds, TechInputs } from '@shared/models';

/**
 * Every reducer module exports selector functions, however child reducers
 * have no knowledge of the overall state tree. To make them usable, we
 * need to make new selectors that wrap them.
 *
 * The createSelector function creates very efficient selectors that are memoized and
 * only recompute when arguments change. The created selectors can also be composed
 * together to select different pieces of state.
 */

export const selectOptionEntitiesState = createSelector(
  fromFeature.inputsState,
  (state) => state
);

export const selectSelectedOptionId = createSelector(
  selectOptionEntitiesState,
  fromInputs.selectOptionId
);

/**
 * Adapters created with @ngrx/entity generate
 * commonly used selector functions including
 * getting all ids in the record set, a dictionary
 * of the records by id, an array of records and
 * the total number of records. This reduces boilerplate
 * in selecting records from the entity state.
 */
export const {
  selectIds: selectOptionIds,
  selectEntities: selectOptionEntities,
  selectAll: selectAllOptions,
  selectTotal: selectTotalOptions,
} = fromInputs.adapter.getSelectors(selectOptionEntitiesState);

export const selectSelectedOption = createSelector(
  selectOptionEntities,
  selectSelectedOptionId,
  (entities, selectedId) => {
    if (environment.defaultData) {
      return {
        id: 1,
        steelMass: 200,
        ballastMass: 400,
        volume: 1400,
        externalArea: 900,
        internalArea: 300,
      };
    } else {
      return selectedId && entities[selectedId];
    }
    // only temporary for dev of CN-101
  }
);

// buoyancy ratios
export const selectBRLower = createSelector(
  selectOptionEntitiesState,
  fromInputs.selectBRLower
);

export const selectBRUpper = createSelector(
  selectOptionEntitiesState,
  fromInputs.selectBRUpper
);

export const selectBRBounds = createSelector(
  selectBRLower,
  selectBRUpper,
  (brLower, brUpper): BRBounds => {
    return {
      brLower,
      brUpper,
    };
  }
);

// Selector to combine the selected entity, br lower and br upper. Used in tech feasibility
export const selectTechInputs = createSelector(
  selectSelectedOption,
  selectBRLower,
  selectBRUpper,
  (entity, lower, upper): TechInputs => {
    return {
      entity,
      lower,
      upper,
    };
  }
);
